import { getStorefrontClient, isValidUrl } from '#libs'
import { isEmpty } from 'lodash'

export type Product = {
  id: string
  title: string
  handle: string
  onlineStoreUrl: string
  tags: string[]
  options: {
    name: string
    optionValues: {
      name: string
    }[]
  }[]
}

export type ColorSwatch = {
  name: string
  background: string
  productUrl: string
  productHandle: string
  productData: Product
}

export const queryGroupProducts = async (groupTag: string) => {
  const client = getStorefrontClient()

  if (!client) {
    // eslint-disable-next-line no-console
    console.error('Failed to get storefront client!')

    return []
  }

  if (isEmpty(groupTag)) {
    // eslint-disable-next-line no-console
    console.error('No group tag found in the product')

    return []
  }

  const query = `query queryProducts($query: String!) {
    products(first: 20, query: $query) {
      nodes {
        id
        title
        handle
        onlineStoreUrl
        tags
        options {
          name
          optionValues {
            name
          }
        }
      }
    }
  }`
  const { data, errors } = await client.request(query, {
    variables: {
      query: `tag:'${groupTag}'`
    }
  })

  if (errors) {
    // eslint-disable-next-line no-console
    console.error(`Failed to query group products!`, errors)

    return []
  }

  return data.products.nodes as Product[]
}

export const getProductSwatchBackground = (product: Product) => {
  const tags = product.tags
  const swatchTag = tags.find((tag) => /swatch:.+/i.test(tag))
  const swatchValue = swatchTag?.split(':').slice(1).join(':').trim()

  if (isValidUrl(swatchValue)) {
    return `url('${swatchValue}')`
  }

  if (swatchValue?.includes('_') && swatchValue?.includes('#')) {
    const [firstColor, secondColor] = swatchValue.split('_')

    return `linear-gradient(to right, ${firstColor} 0%, ${firstColor} 50%, ${secondColor} 50%, ${secondColor} 100%)`
  }

  return swatchValue
}

export const findProductColorOption = (product: Product) => {
  return product.options.find((option) => {
    const name = option.name.toLowerCase()

    return name.includes('color') || name.includes('colour')
  })
}

export const getProductSwatches = (products: Product[]) => {
  return products
    .map((product) => {
      const swatchColorName = findProductColorOption(product)?.optionValues[0]?.name
      const swatchBackground = getProductSwatchBackground(product)

      if (!swatchBackground || !swatchColorName) return

      const productHandle = product.handle
      const productUrl = product.onlineStoreUrl || `/products/${productHandle}`

      return {
        name: swatchColorName,
        background: swatchBackground,
        productUrl,
        productHandle,
        productData: product
      }
    })
    .filter(Boolean) as ColorSwatch[]
}

export const updateProductCardSwatch = async (swatch: ColorSwatch, wrapperEl: HTMLElement | null) => {
  if (!wrapperEl) return

  // Fetch new content
  const productUrl = `${window.Shopify.routes.root}products/${swatch.productData.handle}`
  const endpoint = `${productUrl}?section_id=product-card`
  const request = await fetch(endpoint)
  const response = await request.text()
  const tempEl = document.createElement('div')

  tempEl.innerHTML = response

  const updatedEl = tempEl.querySelector('product-card')

  if (updatedEl) {
    // Insert updated card to DOM
    updatedEl.classList.add('loaded')
    wrapperEl.replaceWith(updatedEl)
    customElements.upgrade(updatedEl)
    document.dispatchEvent(new CustomEvent('product-card:swatch-updated', { detail: { card: updatedEl } }))
  }
}
